import React from 'react';
import { Activity } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import type { Process } from '../type/Process';
import { CollapsibleFormSection } from './CollapsibleFormSection';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { MandatoryFieldLabel } from './MandatoryFieldLabel';

type Props = {
  formProcess: Process;
  setFormProcess: React.Dispatch<React.SetStateAction<Process>>;
  mandatoryFields: string[];
  onToggleMandatory: (key: string) => void;
};

export const ProcessFormMachinedSection: React.FC<Props> = ({
  formProcess,
  setFormProcess,
  mandatoryFields,
  onToggleMandatory,
}) => {
  const { t } = useTranslation();

  const fields = [
    'machinedVolume',
    'machinedDistance',
    'machinedPieces',
    'pieceMachiningDuration',
    'malround',
  ] as const;
  const setCount = fields.filter((f) => formProcess[f] !== null).length;
  const defaultOpen = setCount > 0;

  const countLabel = t('traceability.processManager.form.sections.countLabel', {
    set: setCount,
    total: fields.length,
  });

  const renderLabelRow = (fieldKey: string, labelText: string, inputId: string) => (
    <MandatoryFieldLabel
      fieldKey={fieldKey}
      label={labelText}
      htmlFor={inputId}
      mandatoryFields={mandatoryFields}
      onToggleMandatory={onToggleMandatory}
    />
  );

  return (
    <CollapsibleFormSection
      icon={<Activity className="h-3 w-3" aria-hidden />}
      title={t('traceability.processManager.form.sections.machinedResults.title')}
      toggleLabel={t('traceability.processManager.form.sections.machinedResults.toggleLabel')}
      countLabel={countLabel}
      defaultOpen={defaultOpen}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* machinedVolume — no checkbox (computed) */}
        <SimpleInput
          name="process-machinedVolume"
          type="number"
          min={0}
          label={`${t('traceability.processManager.form.prefillFields.machinedVolume.label')} (${t('traceability.processManager.form.prefillFields.machinedVolume.unit')})`}
          value={formProcess.machinedVolume ?? undefined}
          onChange={(val) =>
            setFormProcess((p) => ({ ...p, machinedVolume: val === null ? null : (val as number) }))
          }
          placeholder={t('traceability.processManager.form.prefillFields.machinedVolume.placeholder')}
        />
        {/* machinedDistance — no checkbox (computed) */}
        <SimpleInput
          name="process-machinedDistance"
          type="number"
          min={0}
          label={`${t('traceability.processManager.form.prefillFields.machinedDistance.label')} (${t('traceability.processManager.form.prefillFields.machinedDistance.unit')})`}
          value={formProcess.machinedDistance ?? undefined}
          onChange={(val) =>
            setFormProcess((p) => ({
              ...p,
              machinedDistance: val === null ? null : (val as number),
            }))
          }
          placeholder={t('traceability.processManager.form.prefillFields.machinedDistance.placeholder')}
        />
        {/* machinedPieces — checkbox with key 'pieces' */}
        <div>
          {renderLabelRow(
            'pieces',
            `${t('traceability.processManager.form.prefillFields.machinedPieces.label')} (${t('traceability.processManager.form.prefillFields.machinedPieces.unit')})`,
            'process-machinedPieces'
          )}
          <SimpleInput
            name="process-machinedPieces"
            type="number"
            min={0}
            value={formProcess.machinedPieces ?? undefined}
            onChange={(val) =>
              setFormProcess((p) => ({
                ...p,
                machinedPieces: val === null ? null : (val as number),
              }))
            }
            placeholder={t('traceability.processManager.form.prefillFields.machinedPieces.placeholder')}
          />
        </div>
        {/* pieceMachiningDuration — no checkbox (computed) */}
        <SimpleInput
          name="process-pieceMachiningDuration"
          type="number"
          min={0}
          label={`${t('traceability.processManager.form.prefillFields.pieceMachiningDuration.label')} (${t('traceability.processManager.form.prefillFields.pieceMachiningDuration.unit')})`}
          value={formProcess.pieceMachiningDuration ?? undefined}
          onChange={(val) =>
            setFormProcess((p) => ({
              ...p,
              pieceMachiningDuration: val === null ? null : (val as number),
            }))
          }
          placeholder={t('traceability.processManager.form.prefillFields.pieceMachiningDuration.placeholder')}
        />
        {/* malround — checkbox with key 'malrond' */}
        <div>
          {renderLabelRow(
            'malrond',
            `${t('traceability.processManager.form.prefillFields.malround.label')} (${t('traceability.processManager.form.prefillFields.malround.unit')})`,
            'process-malround'
          )}
          <SimpleInput
            name="process-malround"
            type="number"
            min={0}
            value={formProcess.malround ?? undefined}
            onChange={(val) =>
              setFormProcess((p) => ({ ...p, malround: val === null ? null : (val as number) }))
            }
            placeholder={t('traceability.processManager.form.prefillFields.malround.placeholder')}
          />
        </div>
      </div>
    </CollapsibleFormSection>
  );
};
